import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A serene beach at sunset with waves gently crashing on the shore",
 *   "durationSeconds": 5,
 *   "aspectRatio": "16:9",
 *   "enhancePrompt": true,
 *   "personGeneration": "allow_adult",
 *   "sampleCount": 1
 * }
 * ```
 */
export interface GoogleVeo20Generate001Params {
  /**
   * 视频生成提示词（必填），描述要生成的视频内容，长度不超过2000字符
   */
  prompt: string;
  /**
   * 输入图像（可选），用于I2V模式，支持URL、base64数据或包含bytesBase64Encoded/gcsUri的对象
   */
  image?: string | any;
  /**
   * 输入视频（可选），用于视频延长模式，支持URL、base64数据或包含bytesBase64Encoded/gcsUri的对象
   */
  video?: string | any;
  /**
   * 视频最后一帧图像（可选），用于视频延长模式，支持URL、base64数据或包含bytesBase64Encoded/gcsUri的对象
   */
  lastFrame?: string | any;
  /**
   * 负向提示词（可选），描述不希望出现的内容
   */
  negativePrompt?: string;
  /**
   * 视频时长（秒），支持5、6、7、8秒，默认5秒
   * @default 5
   * @values 5 | 6 | 7 | 8
   */
  durationSeconds?: 5 | 6 | 7 | 8;
  /**
   * 视频宽高比，支持: 9:16（竖屏）, 16:9（横屏），默认16:9
   * @default "16:9"
   * @values 9:16 | 16:9
   */
  aspectRatio?: '9:16' | '16:9';
  /**
   * 是否自动增强提示词，启用后会优化提示词以获得更好效果，默认true
   * @default true
   */
  enhancePrompt?: boolean;
  /**
   * 人物生成设置，dont_allow（不允许生成人物）, allow_adult（允许生成成年人），默认allow_adult
   * @default "allow_adult"
   * @values dont_allow | allow_adult
   */
  personGeneration?: 'dont_allow' | 'allow_adult';
  /**
   * 生成样本数量，范围1-4，默认1
   * @default 1
   * @range 1 - 4
   */
  sampleCount?: number;
  /**
   * 随机种子，用于可重现的生成结果，范围0-4294967295
   * @range 0 - 4294967295
   */
  seed?: number;
  /**
   * 存储URI（可选），用于指定Google Cloud Storage存储位置（如gs://bucket/path），未指定时返回base64编码
   */
  storageUri?: string;
}

export interface GoogleVeo20Generate001Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_veo_20_generate_001
 *
 * Google Veo 2.0 视频生成接口，支持文本到视频(T2V)、图像到视频(I2V)和视频延长三种模式。
 * 
 * Model: veo-2.0-generate-001
 * Features:
 * - 文本到视频(T2V): 仅提供prompt
 * - 图像到视频(I2V): 提供prompt和image
 * - 视频延长: 提供prompt、video和lastFrame
 * - 支持5-8秒视频时长
 * - 支持9:16和16:9两种宽高比
 * - 支持批量生成1-4个样本
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleVeo20Generate001 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleVeo20Generate001(
 *   {
 *     "prompt": "A serene beach at sunset with waves gently crashing on the shore",
 *     "durationSeconds": 5,
 *     "aspectRatio": "16:9",
 *     "enhancePrompt": true,
 *     "personGeneration": "allow_adult",
 *     "sampleCount": 1
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleVeo20Generate001Params 接口定义
 * @returns 生成的资源列表
 */
export async function googleVeo20Generate001(
  params: GoogleVeo20Generate001Params,
): Promise<GoogleVeo20Generate001Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_veo_20_generate_001',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleVeo20Generate001Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
